/**
 * Presence tracking component.
 *
 * Manages online/offline status, visibility change events, heartbeat interval,
 * and presence data cleanup. Mounted inside SocketProvider.
 */
'use client';

import React, { useEffect, useRef } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { ServerEvents, ClientEvents } from '@voley/shared-types/socket';
import { presenceManager } from '@/api/features/presence';
import { selectCurrentConversationId } from '../../store/slices/messagingUiSlice';
import { useAppSelector } from '../../store/hooks';
import { useSocket } from '../SocketProvider';
import { attachVisibilityChangeListener } from '@/core/utils/windowAttention';

const getCurrentPath = (): string => {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search;
};

export const PresenceInitializer: React.FC<{ connected: boolean; currentUserId?: string }> = ({ connected, currentUserId }) => {
  const { emit, on, off, socket } = useSocket();
  const currentConversationId = useAppSelector(selectCurrentConversationId);
  const lastEmittedConversationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!connected) return;

    const handlePresenceUpdate = (data: { userId?: string; status?: string; lastSeen?: number; lastActivity?: number }) => {
      if (data?.userId && data?.status) {
        presenceManager.updateUserPresence(
          data.userId,
          data.status as 'online' | 'offline' | 'away',
          data.lastSeen || data.lastActivity || undefined
        );
      }
    };

    const handlePresenceBatchUpdate = (data: { presences?: Array<{ userId?: string; status?: string; lastSeen?: number; lastActivity?: number }> }) => {
      if (data?.presences && Array.isArray(data.presences)) {
        data.presences.forEach((presence) => {
          if (presence?.userId && presence?.status) {
            presenceManager.updateUserPresence(
              presence.userId,
              presence.status as 'online' | 'offline' | 'away',
              presence.lastSeen || presence.lastActivity || undefined
            );
          }
        });
      }
    };

    on(ServerEvents.PRESENCE_UPDATE, handlePresenceUpdate);
    on(ServerEvents.PRESENCE_BATCH_UPDATE, handlePresenceBatchUpdate);

    emit(ClientEvents.UPDATE_PRESENCE, {
      status: 'online',
      currentPath: getCurrentPath(),
      tabVisible: typeof document !== 'undefined' ? !document.hidden : true,
      lastActivity: Date.now(),
      isActive: true
    });

    if (currentUserId) {
      presenceManager.updateUserPresence(currentUserId, 'online', Date.now());
    }

    const sendPresenceVisibilityUpdate = () => {
      emit(ClientEvents.UPDATE_PRESENCE, {
        status: 'online',
        currentPath: getCurrentPath(),
        tabVisible: !document.hidden,
        lastActivity: Date.now(),
        isActive: !document.hidden
      });
    };
    const detachVisibilityListener = attachVisibilityChangeListener({
      onVisible: sendPresenceVisibilityUpdate,
      onHidden: sendPresenceVisibilityUpdate,
    });

    const heartbeatInterval = setInterval(() => {
      emit(ClientEvents.UPDATE_PRESENCE, {
        status: 'online',
        currentPath: getCurrentPath(),
        tabVisible: typeof document !== 'undefined' ? !document.hidden : true,
        lastActivity: Date.now(),
        isActive: typeof document !== 'undefined' ? !document.hidden : true
      });
    }, 60_000);

    const cleanupInterval = setInterval(() => {
      presenceManager.cleanup();
    }, 5 * 60 * 1000);

    return () => {
      off(ServerEvents.PRESENCE_UPDATE, handlePresenceUpdate);
      off(ServerEvents.PRESENCE_BATCH_UPDATE, handlePresenceBatchUpdate);
      detachVisibilityListener();
      clearInterval(heartbeatInterval);
      clearInterval(cleanupInterval);
      if (socket?.connected) {
        emit(ClientEvents.UPDATE_PRESENCE, { status: 'offline' });
        if (currentUserId) {
          presenceManager.updateUserPresence(currentUserId, 'offline', Date.now());
        }
      }
    };
  }, [connected, emit, on, off, socket, currentUserId]);

  // Track conversation changes
  useEffect(() => {
    if (!connected) return;
    if (lastEmittedConversationRef.current !== currentConversationId) {
      lastEmittedConversationRef.current = currentConversationId;
      emit(ClientEvents.UPDATE_PRESENCE, {
        status: 'online',
        currentPath: getCurrentPath(),
        tabVisible: typeof document !== 'undefined' ? !document.hidden : true,
        lastActivity: Date.now(),
        isActive: true
      });
      reduxLogger.info('[PresenceInitializer] Emitted presence update for conversation change:', currentConversationId);
    }
  }, [connected, currentConversationId, emit]);

  return null;
};
